import { ComponentProps, FC, useMemo } from 'react';

import { lowerCase, startCase } from 'lodash';
import dayjs from 'dayjs';
import cn from 'classnames';
import { Avatar } from 'ui/components/img';
import { AvatarGroup } from 'ui/components/img/Avatar';
import { SIZE } from 'utils/constants';
import { Lead } from 'utils/types';

import Status from '../Status';

type STATUS_TYPE = ComponentProps<typeof Status>;

type PROPS = {
  data: {
    hideAssociatedContact?: boolean;
    lead: Lead;
    priority: string;
    taskType: string;
    title: string;
    status: string;
    dueAt?: string;
    accountUsers: Array<{
      accountId: string;
      createdAt: string;
      emailAddress: string;
      firstName: string;
      fullName: string;
      isEmailAddressVerified: boolean;
      isMfaEnabled: boolean;
      landlineNumber: string;
      lastName: string;
      password: string;
      passwordChangedAt: string;
      phoneNumber: string;
      photoUrl: string;
      recoveryMobileNumber: string;
      roleId: string;
      source: string;
      status: string;
      trashed: boolean;
      trashedAt: string;
      updatedAt: string;
    }>;
  };
};

const TaskEntry: FC<PROPS> = ({ data }) => {
  const {
    taskType,
    title,
    status = '',
    priority = '',
    dueAt,
    accountUsers = [],
    lead,
    hideAssociatedContact,
  } = data;

  const taskTypeLabel = useMemo(() => {
    if (taskType === 'TODO') return 'TO DO';
    return taskType;
  }, [taskType]);

  const statusMap = {
    low: {
      label: 'Low',
      type: 'close',
    },
    medium: {
      label: 'Medium',
      type: 'warning',
    },
    high: {
      label: 'High',
      type: 'urgent',
    },
  };

  const formattedDueAt = dayjs(dueAt).format('DD MMM YYYY');
  return (
    <div className="py-[18px] text-secondary-dark space-y-4">
      <div className="space-y-[5px]">
        <div className="font-semibold text-xs leading-5 tracking-[0.48px]">
          {taskTypeLabel}
        </div>
        <div className="flex gap-[10px]">
          <div className="flex-auto">
            <div className="font-semibold leading-6 tracking-[0.64px] ">
              {title}
            </div>
            <span>
              <span className="font-semibold text-info-base">
                {startCase(lowerCase(status))}{' '}
              </span>
              Due Date: {formattedDueAt}
            </span>
          </div>
          <Status
            className="self-end"
            type={
              statusMap[priority?.toLowerCase() as keyof typeof statusMap]
                ?.type as STATUS_TYPE['type']
            }
            label={
              statusMap[priority?.toLowerCase() as keyof typeof statusMap]
                ?.label
            }
          />
        </div>
      </div>
      <div className="flex items-center justify-between">
        {!hideAssociatedContact && (
          <div className="flex items-center w-full gap-[14px]">
            <Avatar className={cn('md', 'flex-shrink-0')} user={lead} />
            <div className="truncate">
              <div className={cn('text-xs leading-[22px] tracking-[0.04em]')}>
                Associated Contact
              </div>
              <div
                className={cn(
                  'text-sm font-semibold leading-[22px] tracking-[0.04em]',
                  'truncate',
                )}
              >
                {lead.firstName} {`${lead?.lastName?.[0] ?? ''}.` || ''}
              </div>
            </div>
          </div>
        )}
        <div className={cn('flex items-center gap-2', 'flex-1', 'justify-end')}>
          <div className="whitespace-nowrap">Assigned to:</div>
          <AvatarGroup size={SIZE.Small} users={accountUsers} />
        </div>
      </div>
    </div>
  );
};

export default TaskEntry;
